import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { OrderHardware, OrderAddon } from '../../types/order';
import { ORDER_BAR_BG, ORDER_BAR_TEXT, ORDER_BAR_TEXT_MUTED } from './OrderPageHeader';

const API_BASE = process.env.REACT_APP_API_URL || window.location.origin;
const CARD_WIDTH = 320;
const SCROLL_GAP = 16;

const CONTRACT_OPTIONS = [
  { value: 'no_contract', label: 'No Contract' },
  { value: '12_month', label: '12 Month Contract' },
  { value: '24_month', label: '24 Month Contract' },
];

interface EquipmentSelectionProps {
  hardware: OrderHardware[];
  selectedHardware: OrderHardware[];
  onHardwareChange: (hardware: OrderHardware[]) => void;
  addOns: OrderAddon[];
  selectedAddOns: OrderAddon[];
  onAddOnsChange: (addOns: OrderAddon[]) => void;
  contractType: string;
  onContractTypeChange: (type: string) => void;
}

function getContractPrice(item: OrderHardware, contractType: string): number {
  if (contractType === 'no_contract' && item.price_no_contract != null) {
    return Number(item.price_no_contract);
  }
  if (contractType === '12_month' && item.price_12_month != null) {
    return Number(item.price_12_month);
  }
  if (contractType === '24_month' && item.price_24_month != null) {
    return Number(item.price_24_month);
  }
  return item.effective_price ?? item.price ?? 0;
}

function EquipmentSelection({
  hardware,
  selectedHardware,
  onHardwareChange,
  addOns,
  selectedAddOns,
  onAddOnsChange,
  contractType,
  onContractTypeChange,
}: EquipmentSelectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSelect = (item: OrderHardware) => {
    const isSelected = selectedHardware.some((h) => h.id === item.id);
    if (isSelected) {
      onHardwareChange(selectedHardware.filter((h) => h.id !== item.id));
    } else {
      onHardwareChange([...selectedHardware, item]);
    }
  };

  const handleAddOnToggle = (item: OrderAddon) => {
    const isSelected = selectedAddOns.some((p) => p.id === item.id);
    if (isSelected) {
      onAddOnsChange(selectedAddOns.filter((p) => p.id !== item.id));
    } else {
      onAddOnsChange([...selectedAddOns, item]);
    }
  };

  const formatPrice = (price?: number) => {
    if (price == null) return 'N/A';
    return `$${Number(price).toFixed(2)}`;
  };

  const getImageUrl = (item: OrderHardware) => {
    if (!item.image_path) return null;
    return `${API_BASE.replace(/\/$/, '')}/api/orders/hardware-images/${item.id}`;
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = (CARD_WIDTH + SCROLL_GAP) * (direction === 'left' ? -1 : 1);
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const headerText = { color: ORDER_BAR_TEXT };
  const headerTextMuted = { color: ORDER_BAR_TEXT_MUTED };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Contract Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Select your service contract length. This applies to your entire service order.
        Longer contracts may offer reduced equipment pricing.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <ToggleButtonGroup
          value={contractType}
          exclusive
          onChange={(_, v) => { if (v) onContractTypeChange(v); }}
          fullWidth
          size="small"
        >
          {CONTRACT_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ fontWeight: 600 }}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      <Typography variant="h6" fontWeight={600} gutterBottom>
        Select Equipment (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        You can select one of each equipment item. Equipment is a one-time purchase. Delivery fee is per order.
      </Typography>

      {hardware.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No equipment available at this time
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: -12,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'grey.200',
              width: 40,
              height: 40,
              '&:hover': { bgcolor: 'grey.300' },
              '&.Mui-disabled': { bgcolor: 'grey.100' },
            }}
            disabled={hardware.length === 0}
            size="small"
            aria-label="Scroll left"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: -12,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'grey.200',
              width: 40,
              height: 40,
              '&:hover': { bgcolor: 'grey.300' },
              '&.Mui-disabled': { bgcolor: 'grey.100' },
            }}
            disabled={hardware.length === 0}
            size="small"
            aria-label="Scroll right"
          >
            <ChevronRightIcon />
          </IconButton>

          <Box
            ref={scrollRef}
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2,
              pt: 0.5,
              px: 0.5,
              scrollSnapType: 'x mandatory',
              scrollBehavior: 'smooth',
              '& > *': { scrollSnapAlign: 'start', flexShrink: 0 },
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-thumb': { borderRadius: 4, bgcolor: 'action.hover' },
            }}
          >
            {hardware.map((item) => {
              const isSelected = selectedHardware.some((h) => h.id === item.id);
              const contractPrice = getContractPrice(item, contractType);
              const basePrice = item.effective_price ?? item.price ?? 0;
              const hasContractDiscount = Number(contractPrice) < Number(basePrice);
              const imgUrl = getImageUrl(item);
              const typeLabel =
                item.hardware_type === 'router'
                  ? 'Router'
                  : item.hardware_type === 'ata'
                  ? 'ATA'
                  : 'Equipment';

              return (
                <Card
                  key={item.id}
                  variant="outlined"
                  sx={{
                    width: CARD_WIDTH,
                    minWidth: CARD_WIDTH,
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    boxShadow: isSelected ? 4 : 0,
                    bgcolor: isSelected ? 'action.selected' : 'background.paper',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header band */}
                  <Box
                    sx={{
                      bgcolor: ORDER_BAR_BG,
                      color: ORDER_BAR_TEXT,
                      px: 2,
                      pt: 2,
                      pb: 1.5,
                      borderRadius: 0,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} sx={headerText} gutterBottom>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={headerTextMuted}>
                      {typeLabel}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ mt: 1, ...headerText, fontSize: '1.125rem' }}
                    >
                      {hasContractDiscount ? (
                        <>
                          <Typography
                            component="span"
                            sx={{ textDecoration: 'line-through', ...headerTextMuted, fontSize: '0.9rem', mr: 0.5 }}
                          >
                            {formatPrice(basePrice)}
                          </Typography>
                          {formatPrice(contractPrice)}
                        </>
                      ) : (
                        formatPrice(contractPrice)
                      )}
                    </Typography>
                    {item.delivery_fee != null && Number(item.delivery_fee) > 0 && (
                      <Typography variant="body2" sx={{ ...headerTextMuted, mt: 0.25 }}>
                        + {formatPrice(item.delivery_fee)} delivery
                      </Typography>
                    )}
                  </Box>

                  {imgUrl && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={imgUrl}
                      alt={item.name}
                      sx={{ objectFit: 'contain', bgcolor: 'grey.50', p: 1 }}
                    />
                  )}

                  <CardContent sx={{ flex: 1, pb: 0 }}>
                    {item.description &&
                      item.description.replace(/<[^>]*>/g, '').trim().length > 0 && (
                      <Box
                        sx={{
                          '& p': { margin: 0, mb: 0.5, '&:last-child': { mb: 0 } },
                          '& ul': { m: 0, pl: 2, mb: 0.5 },
                          '& ol': { m: 0, pl: 2, mb: 0.5 },
                          '& a': { color: 'primary.main' },
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                          lineHeight: 1.5,
                        }}
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      />
                    )}
                  </CardContent>

                  <Box sx={{ px: 2, py: 2 }}>
                    <Button
                      fullWidth
                      variant={isSelected ? 'contained' : 'outlined'}
                      color="primary"
                      size="medium"
                      onClick={() => handleSelect(item)}
                      sx={{ fontWeight: 600 }}
                    >
                      {isSelected ? 'Selected' : 'Select'}
                    </Button>
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {addOns.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Add-ons (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Optional extras billed with your monthly service.
          </Typography>
          {addOns.map((item) => {
            const isSelected = selectedAddOns.some((p) => p.id === item.id);
            return (
              <Paper
                key={item.id}
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'action.selected' : 'background.paper',
                }}
              >
                <FormControlLabel
                  sx={{ flex: 1, mr: 2 }}
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleAddOnToggle(item)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {item.name}
                      </Typography>
                      {item.description && (
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Typography variant="body1" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                  {formatPrice(item.monthly_price)}/mo
                </Typography>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default EquipmentSelection;
